import classNames from 'classnames';
import {isTabletByScreen} from 'core/helpers';
import {CityClarificationPopup} from 'modules/locations/components/CityClarificationPopup';
import {MODALS} from 'modules/modals/constants';
import {selectIsPortalVisible} from 'modules/portal/selectors';
import {hideModal, showModal} from 'modules/modals/thunks';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import style from './style.module.scss';
import {CollapseAnimatedOnTop} from 'components/CollapseAnimatedOnTop';
import {useCityClarification} from 'modules/locations/hooks/useCityClarification';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {Portal} from 'modules/portal/components/Portal';
import {useCheckScreenMedia} from 'core/hooks/useCheckScreenMedia';
import {TABLET_MAX_WIDTH} from 'core/constants';
import {useAddressesRoutes} from 'core/modules/routing/hooks/useAddressesRoutes';
import {selectIsCityApplied} from 'modules/locations/selectors';
import {CookieService} from 'core/services/CookieService';
import {CITY_APPLIED_STORE_FLAG} from 'modules/locations/constants';
import {CollapseAnimatedOnScrollBottom} from 'components/CollapseAnimatedOnScrollBottom';
import {useScroll} from 'core/hooks/useScroll';
import {HEADER_STICKY_ID} from 'components/Header/BasicHeader/constants';
import {HeaderTop} from './components/HeaderTop';
import {HeaderBottom} from './components/HeaderBottom';
import {HeaderExtraLinks} from './components/HeaderExtraLinks';
import {useAdaptTopMenuPosition} from 'components/Header/hooks/useAdaptTopMenuPosition';
import {ExpressOrderPopup} from 'modules/express-order/components/ExpressOrderPopup';
import {Menu} from 'components/Header/BasicHeader/components/Menu';
import {useGetAbExpressOrder} from 'modules/ab-tests/hooks';
import {useInitOnboarding} from 'modules/onboarding/hooks';
import {useStockExtraLinksHidden} from 'modules/stock-x/hooks/useStockExtraLinksHidden';

interface IProps {
    headerNoShadow?: boolean;
    isHiddenOnMobile?: boolean;
}

export const Header: FC<IProps> = ({headerNoShadow = false, isHiddenOnMobile = false}) => {
    const dispatch = useAppDispatch();
    const isCityApplied = useAppSelector(selectIsCityApplied);
    const isStockExtraLinksHidden = useStockExtraLinksHidden();

    const isNotAtTop = useScroll(1, 0);

    useCityClarification();

    const isMenuOpen = useAppSelector(selectIsPortalVisible(MODALS.CATALOG_MENU.name));
    const isTabletScreen = useCheckScreenMedia(isTabletByScreen(), TABLET_MAX_WIDTH);
    const {isActive: isAbExpressOrder} = useGetAbExpressOrder();

    const {
        list: {url: addressListUrl},
    } = useAddressesRoutes();

    const [isHeaderTopHidden, setIsHeaderTopHidden] = useState<boolean>(false);
    const [isMenuTabletOpen, setIsMenuTabletOpen] = useState<boolean>(false);
    const [isCitiesMenuOpen, setIsCitiesMenuOpen] = useState<boolean>(false);
    const [isSearchPanelVisible, setIsSearchPanelVisible] = useState<boolean>(false);

    const handleCatalogClick = useCallback(() => {
        if (isMenuOpen) {
            dispatch(hideModal(MODALS.CATALOG_MENU.name));
            return;
        }

        dispatch(showModal(MODALS.CATALOG_MENU.name));
    }, [dispatch, isMenuOpen]);

    const handleHeaderClick = useCallback(() => {
        if (isMenuOpen) {
            dispatch(hideModal(MODALS.CATALOG_MENU.name));
        }
    }, [dispatch, isMenuOpen]);

    const setIsMenuOpen = (value: boolean) => {
        setIsMenuTabletOpen(value);
    };

    const handleHeaderClickCollapsed = (isCollapsed: boolean) => {
        setIsHeaderTopHidden(isCollapsed);
    };

    const handleChooseAnotherCity = () => {
        setIsMenuTabletOpen(true);
        setIsCitiesMenuOpen(true);
    };

    // useEffect(() => {
    //     const shouldHideCatalog = isSearchPanelVisible || isTabletScreen;
    //
    // if (shouldHideCatalog) {
    //     dispatch(hideModal(MODALS.CATALOG_MENU.name));
    // }
    // }, [dispatch, isSearchPanelVisible, isTabletScreen]);

    const cookieService = CookieService.getInstance();
    const isClarificationCityPopupHidden = useMemo(() => {
        return Boolean(null === isCityApplied ? cookieService.get(CITY_APPLIED_STORE_FLAG) : isCityApplied);
    }, [cookieService, isCityApplied]);

    useInitOnboarding(isClarificationCityPopupHidden && isAbExpressOrder);

    useAdaptTopMenuPosition(isHeaderTopHidden);

    const siteMenu = (isBurgerHidden = false) => (
        <Menu
            isBurgerHidden={isBurgerHidden}
            isCitiesMenuOpen={isCitiesMenuOpen}
            isMenuOpen={isMenuTabletOpen}
            setIsCitiesMenuOpen={setIsCitiesMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
        />
    );

    return (
        <header
            id={'header'}
            className={classNames(style.root, {
                [style.hiddenOnMobile]: isHiddenOnMobile,
            })}
        >
            <div className={classNames(style.header)} onClick={handleHeaderClick}>
                <div
                    className={classNames(style.headerFixed, {
                        [style.isNotAtTop]: isNotAtTop && !isMenuOpen,
                        [style.noShadow]: headerNoShadow,
                    })}
                >
                    <div className={style.standardHeaderContent}>
                        {isAbExpressOrder && (
                            <div className={style.headerActions}>
                                <ExpressOrderPopup resolution={'MOBILE'} />
                            </div>
                        )}
                        <CollapseAnimatedOnTop
                            className={classNames(style.hiddenContainerSm, style.topCollapsedMenu)}
                            onCollapsed={handleHeaderClickCollapsed}
                        >
                            <HeaderTop isHeaderTopHidden={isHeaderTopHidden} addressListUrl={addressListUrl} />
                        </CollapseAnimatedOnTop>
                        <HeaderBottom
                            isSearchPanelVisible={isSearchPanelVisible}
                            isMenuOpen={isMenuOpen}
                            handleCatalogClick={handleCatalogClick}
                            setIsSearchPanelVisible={setIsSearchPanelVisible}
                            menu={siteMenu()}
                        />
                        {!isStockExtraLinksHidden && (
                            <CollapseAnimatedOnScrollBottom
                                className={classNames(style.hiddenContainerSm, {
                                    [style.hiddenBottomMenu]: isMenuOpen,
                                })}
                                isHidden={isMenuOpen}
                            >
                                <HeaderExtraLinks />
                            </CollapseAnimatedOnScrollBottom>
                        )}
                    </div>
                    <div id={HEADER_STICKY_ID} />
                </div>
                {!isClarificationCityPopupHidden && isTabletScreen && (
                    <Portal name={MODALS.CITY_CLARIFICATION.name} willMount={true}>
                        <div className={style.headerCityPopupSm}>
                            <CityClarificationPopup chooseAnotherCity={handleChooseAnotherCity} />
                        </div>
                    </Portal>
                )}
            </div>
        </header>
    );
};
